import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, PencilSimple, Star, StarHalf, Phone, EnvelopeSimple, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const formatDateDMY = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const getCatLabel = (cat, t) => {
  if (!cat || cat === 'uncategorized') return t('uncategorized');
  const key = `cat_${cat}`;
  const label = t(key);
  return label !== key ? label : cat;
};

export default function VendorDetails() {
  const { vendorId } = useParams();
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [buildings, setBuildings] = useState([]);

  const fetchVendor = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendors/${vendorId}`);
      setVendor(res.data);
      setForm({ name: res.data.name, phone: res.data.phone || '', email: res.data.email || '', notes: res.data.notes || '' });
    } catch (err) { toast.error(t('errorOccurred')); navigate('/vendors'); }
  }, [vendorId, t, navigate]);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buildings`);
      setBuildings(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchVendor(); fetchBuildings(); }, [fetchVendor, fetchBuildings]);

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/vendors/${vendorId}`, form);
      toast.success(t('savedSuccess'));
      setEditOpen(false);
      fetchVendor();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const togglePreferred = async (buildingId) => {
    try {
      await axios.post(`${API}/vendors/${vendorId}/toggle-preferred/${buildingId}`);
      fetchVendor();
    } catch (err) { toast.error(t('errorOccurred')); }
  };

  const toggleBuilding = (bid) => setExpandedBuildings(prev => ({ ...prev, [bid]: !prev[bid] }));

  if (!vendor) return <Layout><div className="p-6 text-zinc-500">Loading...</div></Layout>;

  const preferred = vendor.preferred_buildings || [];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500" data-testid="vendor-breadcrumb">
          <button onClick={() => navigate('/vendors')} className="hover:text-zinc-900 transition-colors font-medium" data-testid="breadcrumb-vendors">{t('vendors')}</button>
          <CaretRight size={12} className="text-zinc-300" />
          <span className="text-zinc-800 font-semibold truncate max-w-xs">{vendor.name}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/vendors')} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="back-to-vendors"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" data-testid="vendor-detail-name">{vendor.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                {vendor.phone && <span className="flex items-center gap-1"><Phone size={14} />{vendor.phone}</span>}
                {vendor.email && <span className="flex items-center gap-1"><EnvelopeSimple size={14} />{vendor.email}</span>}
              </div>
              {vendor.notes && <p className="text-sm text-zinc-400 mt-1 max-w-lg">{vendor.notes}</p>}
            </div>
          </div>
          {isAdmin && (
            <Button onClick={() => setEditOpen(true)} variant="outline" className="rounded-none" data-testid="edit-vendor-btn">
              <PencilSimple size={16} className="mr-2" />{t('edit')}
            </Button>
          )}
        </div>

        {/* Financial Summary — Total Expenses + Buildings only */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="vendor-summary">
          <div className="bg-white border border-zinc-200 p-5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{t('totalExpenses')}</p>
            <p className="text-2xl font-bold font-mono mt-2" data-testid="vendor-total">&euro;{(vendor.total_expenses || 0).toFixed(2)}</p>
            <p className="text-xs text-zinc-400 mt-1">{vendor.expense_count || 0} {t('expenses')}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{t('buildings')}</p>
            <p className="text-2xl font-bold font-mono mt-2" data-testid="vendor-buildings-count">{(vendor.expenses_by_building || []).length}</p>
          </div>
        </div>

        {/* Expenses by Building */}
        <div>
          <h2 className="text-sm font-semibold text-zinc-800 uppercase tracking-wider mb-3">{t('expensesByBuilding')}</h2>
          {(!vendor.expenses_by_building || vendor.expenses_by_building.length === 0) ? (
            <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noExpenses')}</div>
          ) : (
            <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
              {vendor.expenses_by_building.map(bg => {
                const isOpen = expandedBuildings[bg.building_id] !== false;
                const isPref = preferred.includes(bg.building_id);
                return (
                  <div key={bg.building_id} data-testid={`vendor-building-${bg.building_id}`}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50/50 transition-colors">
                      <button onClick={() => toggleBuilding(bg.building_id)} className="flex items-center gap-3 flex-1 text-left" data-testid={`toggle-building-${bg.building_id}`}>
                        <span className="text-zinc-400 flex-shrink-0">{isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}</span>
                        <span className="text-sm font-medium text-zinc-800 truncate">{bg.building_name}</span>
                        <span className="text-[11px] text-zinc-400 font-mono">({bg.expenses.length})</span>
                      </button>
                      {isAdmin && (
                        <button onClick={() => togglePreferred(bg.building_id)} title={t('preferredVendor')} className={`p-1 transition-colors ${isPref ? 'text-amber-500 hover:text-amber-600' : 'text-zinc-300 hover:text-amber-400'}`} data-testid={`preferred-${bg.building_id}`}>
                          <Star size={14} weight={isPref ? 'fill' : 'regular'} />
                        </button>
                      )}
                      <span className="text-sm font-mono font-semibold text-zinc-800 flex-shrink-0">&euro;{bg.total.toFixed(2)}</span>
                    </div>
                    {isOpen && (
                      <div className="border-t border-zinc-100 bg-zinc-50/30">
                        <table className="w-full text-xs" data-testid={`vendor-building-table-${bg.building_id}`}>
                          <thead>
                            <tr className="text-[10px] text-zinc-400 uppercase tracking-wider">
                              <th className="px-4 py-2 text-left font-medium w-24">{t('date')}</th>
                              <th className="px-4 py-2 text-left font-medium">{t('category')}</th>
                              <th className="px-4 py-2 text-left font-medium">{t('notes')}</th>
                              <th className="px-4 py-2 text-right font-medium w-24">{t('amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bg.expenses.map(exp => (
                              <tr key={exp.expense_id} className="border-t border-zinc-100" data-testid={`vendor-exp-${exp.expense_id}`}>
                                <td className="px-4 py-2 font-mono text-zinc-500">{formatDateDMY(exp.date)}</td>
                                <td className="px-4 py-2 text-zinc-700">{getCatLabel(exp.category, t)}</td>
                                <td className="px-4 py-2 text-zinc-500 truncate max-w-xs">{exp.notes || '-'}</td>
                                <td className="px-4 py-2 text-right font-mono font-semibold text-zinc-800">&euro;{(exp.amount || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-zinc-100/50 border-t border-zinc-200">
                              <td colSpan={3} className="px-4 py-2 text-right text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{t('total')}</td>
                              <td className="px-4 py-2 text-right font-mono font-bold text-zinc-900">&euro;{bg.total.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader><DialogTitle>{t('editVendor')}</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div><Label>{t('vendorName')} *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="rounded-none mt-1" data-testid="vendor-edit-name" /></div>
              <div><Label>{t('phone')}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" data-testid="vendor-edit-phone" /></div>
              <div><Label>{t('email')}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="vendor-edit-email" /></div>
              <div><Label>{t('notes')}</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border border-zinc-200 p-2 text-sm min-h-[60px] mt-1" data-testid="vendor-edit-notes" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="vendor-edit-submit">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
